import { Graphics } from 'pixi.js';
import { TILE_SIZE } from '../main.js';
import { WALKABLE_TILES } from '../systems/DungeonGenerator.js';

/**
 * Ability definitions for each class.
 */
const ABILITIES = {
  ironclad: {
    name: 'Shield Bash',
    cooldown: 4,
    description: 'Dash and stun nearby enemies',
    execute: executeShieldBash,
  },
  riftmage: {
    name: 'Blink',
    cooldown: 5,
    description: 'Teleport and explode on arrival',
    execute: executeBlink,
  },
  phantom: {
    name: 'Shadow Step',
    cooldown: 3,
    description: 'Teleport behind nearest enemy',
    execute: executeShadowStep,
  },
  voidpriest: {
    name: 'Radiant Ward',
    cooldown: 6,
    description: 'Shield, heal, and repel enemies',
    execute: executeRadiantWard,
  },
};

/**
 * Manages class abilities with cooldown tracking and execution.
 */
export class AbilitySystem {
  constructor(eventBus) {
    this.eventBus = eventBus;
    this.cooldowns = {}; // classId -> remaining cooldown in seconds
  }

  /**
   * Execute the ability for the given class.
   * @param {string} classId - The class identifier
   * @param {Player} player - The player entity
   * @param {Enemy[]} enemies - Array of alive enemies
   * @param {number[][]} tiles - Dungeon tile grid
   * @returns {{ effects: object[], cooldown: number }|null} Result or null if not ready
   */
  executeAbility(classId, player, enemies, tiles) {
    if (!this.isReady(classId)) return null;

    const ability = ABILITIES[classId];
    if (!ability) return null;

    const result = ability.execute(player, enemies, tiles);

    // Start cooldown
    this.cooldowns[classId] = ability.cooldown;

    return result;
  }

  /**
   * Check if the ability for the given class is off cooldown.
   * @param {string} classId
   * @returns {boolean}
   */
  isReady(classId) {
    return !this.cooldowns[classId] || this.cooldowns[classId] <= 0;
  }

  /**
   * Get remaining cooldown for a class ability.
   * @param {string} classId
   * @returns {number} Remaining seconds (0 if ready)
   */
  getCooldown(classId) {
    return Math.max(0, this.cooldowns[classId] || 0);
  }

  /**
   * Get ability info for a class.
   * @param {string} classId
   * @returns {{ name: string, cooldown: number, description: string }|null}
   */
  getAbilityInfo(classId) {
    const ability = ABILITIES[classId];
    if (!ability) return null;
    return { name: ability.name, cooldown: ability.cooldown, description: ability.description };
  }

  /**
   * Tick all cooldowns down by dt seconds.
   * @param {number} dt - Delta time in seconds
   */
  update(dt) {
    for (const classId in this.cooldowns) {
      if (this.cooldowns[classId] > 0) {
        this.cooldowns[classId] -= dt;
        if (this.cooldowns[classId] < 0) {
          this.cooldowns[classId] = 0;
        }
      }
    }
  }
}

// ============================================================
// Ability Implementations
// ============================================================

/**
 * Helper: check if a pixel position is on a walkable tile.
 */
function isWalkable(px, py, tiles) {
  const col = Math.floor(px / TILE_SIZE);
  const row = Math.floor(py / TILE_SIZE);
  if (row < 0 || row >= tiles.length || col < 0 || col >= tiles[0].length) return false;
  return WALKABLE_TILES.has(tiles[row][col]);
}

/**
 * Helper: check if a bounding box at (x, y) with the given size is fully walkable.
 */
function canOccupy(x, y, size, tiles) {
  const pad = 1;
  const offset = (TILE_SIZE - size) / 2;
  const left = x + offset + pad;
  const right = x + offset + size - pad - 1;
  const top = y + offset + pad;
  const bottom = y + offset + size - pad - 1;
  return (
    isWalkable(left, top, tiles) &&
    isWalkable(right, top, tiles) &&
    isWalkable(left, bottom, tiles) &&
    isWalkable(right, bottom, tiles)
  );
}

/**
 * Helper: find nearest walkable position within maxDist of (targetX, targetY).
 * Searches in a spiral pattern outward.
 */
function findNearestWalkable(targetX, targetY, size, tiles, maxDist) {
  // Check if target itself is walkable
  if (canOccupy(targetX, targetY, size, tiles)) {
    return { x: targetX, y: targetY };
  }

  // Search in expanding rings
  const step = TILE_SIZE / 2;
  for (let dist = step; dist <= maxDist; dist += step) {
    for (let angle = 0; angle < Math.PI * 2; angle += Math.PI / 8) {
      const testX = targetX + Math.cos(angle) * dist;
      const testY = targetY + Math.sin(angle) * dist;
      if (canOccupy(testX, testY, size, tiles)) {
        return { x: testX, y: testY };
      }
    }
  }

  return null; // No walkable position found
}

/**
 * Helper: get normalized facing direction. Returns {nx, ny} or null if zero.
 */
function getFacing(player) {
  const fx = player.facingX;
  const fy = player.facingY;
  const len = Math.sqrt(fx * fx + fy * fy);
  if (len === 0) return null;
  return { nx: fx / len, ny: fy / len };
}

/**
 * Helper: distance between two points.
 */
function dist(x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
}

// ============================================================
// Ironclad -- Shield Bash
// ============================================================

function executeShieldBash(player, enemies, tiles) {
  const facing = getFacing(player);
  if (!facing) return { effects: [], cooldown: 4 };

  const effects = [];
  const pCenterX = player.x + TILE_SIZE / 2;
  const pCenterY = player.y + TILE_SIZE / 2;

  // Dash forward 32px
  const dashDist = 32;
  let destX = player.x + facing.nx * dashDist;
  let destY = player.y + facing.ny * dashDist;

  // Check if destination is walkable, if not find nearest
  if (!canOccupy(destX, destY, player.size, tiles)) {
    const fallback = findNearestWalkable(destX, destY, player.size, tiles, dashDist);
    if (fallback) {
      destX = fallback.x;
      destY = fallback.y;
    } else {
      destX = player.x;
      destY = player.y;
    }
  }

  player.x = destX;
  player.y = destY;
  player._syncVisual();

  // 1s invulnerability during the bash
  player.hitInvulnTimer = Math.max(player.hitInvulnTimer, 1.0);

  // Impact point (center of destination)
  const impactX = destX + TILE_SIZE / 2;
  const impactY = destY + TILE_SIZE / 2;

  // Damage and stun enemies within 24px radius at destination
  const stunRadius = 24;
  for (const enemy of enemies) {
    if (!enemy.isAlive || enemy.state === 'dead') continue;
    const eCenterX = enemy.x + TILE_SIZE / 2;
    const eCenterY = enemy.y + TILE_SIZE / 2;
    const d = dist(impactX, impactY, eCenterX, eCenterY);
    if (d <= stunRadius) {
      // Deal 20 damage
      const kbDx = eCenterX - impactX;
      const kbDy = eCenterY - impactY;
      const kbLen = Math.sqrt(kbDx * kbDx + kbDy * kbDy);
      const kbNx = kbLen > 0 ? kbDx / kbLen : facing.nx;
      const kbNy = kbLen > 0 ? kbDy / kbLen : facing.ny;
      enemy.takeDamage(20, kbNx, kbNy);
      // Stun for 1.5s via status effect system (M18/M20)
      enemy.stunTimer = 1.5;
      effects.push({ type: 'damage', target: enemy, amount: 20, x: eCenterX, y: eCenterY });
      effects.push({ type: 'apply_status', target: enemy, effectId: 'stunned' });
    }
  }

  // Visual: bright blue flash circle at impact
  effects.push({ type: 'flash_circle', x: impactX, y: impactY, radius: stunRadius, color: 0x4488ff, duration: 0.3 });

  return { effects, cooldown: 4 };
}

// ============================================================
// Riftmage -- Blink
// ============================================================

function executeBlink(player, enemies, tiles) {
  const facing = getFacing(player);
  if (!facing) return { effects: [], cooldown: 5 };

  const effects = [];
  const startX = player.x + TILE_SIZE / 2;
  const startY = player.y + TILE_SIZE / 2;

  // Teleport 64px in facing direction
  const blinkDist = 64;
  let destX = player.x + facing.nx * blinkDist;
  let destY = player.y + facing.ny * blinkDist;

  // If destination not walkable, find nearest walkable tile within 64px
  if (!canOccupy(destX, destY, player.size, tiles)) {
    const fallback = findNearestWalkable(destX, destY, player.size, tiles, 64);
    if (fallback) {
      destX = fallback.x;
      destY = fallback.y;
    } else {
      destX = player.x;
      destY = player.y;
    }
  }

  // Purple flash at start position
  effects.push({ type: 'flash_circle', x: startX, y: startY, radius: 12, color: 0xaa44ff, duration: 0.3 });

  // Particle trail between start and end
  effects.push({
    type: 'particle_trail',
    startX: startX,
    startY: startY,
    endX: destX + TILE_SIZE / 2,
    endY: destY + TILE_SIZE / 2,
    color: 0xaa44ff,
    duration: 0.4,
  });

  player.x = destX;
  player.y = destY;
  player._syncVisual();

  // AOE explosion at arrival: 32px radius, 25 damage
  const arrivalX = destX + TILE_SIZE / 2;
  const arrivalY = destY + TILE_SIZE / 2;
  const explosionRadius = 32;

  for (const enemy of enemies) {
    if (!enemy.isAlive || enemy.state === 'dead') continue;
    const eCenterX = enemy.x + TILE_SIZE / 2;
    const eCenterY = enemy.y + TILE_SIZE / 2;
    const d = dist(arrivalX, arrivalY, eCenterX, eCenterY);
    if (d <= explosionRadius) {
      const kbDx = eCenterX - arrivalX;
      const kbDy = eCenterY - arrivalY;
      const kbLen = Math.sqrt(kbDx * kbDx + kbDy * kbDy);
      const kbNx = kbLen > 0 ? kbDx / kbLen : facing.nx;
      const kbNy = kbLen > 0 ? kbDy / kbLen : facing.ny;
      enemy.takeDamage(25, kbNx, kbNy);
      effects.push({ type: 'damage', target: enemy, amount: 25, x: eCenterX, y: eCenterY });
    }
  }

  // Purple flash at end position
  effects.push({ type: 'flash_circle', x: arrivalX, y: arrivalY, radius: explosionRadius, color: 0xaa44ff, duration: 0.3 });

  return { effects, cooldown: 5 };
}

// ============================================================
// Phantom -- Shadow Step
// ============================================================

function executeShadowStep(player, enemies, tiles) {
  const facing = getFacing(player);
  if (!facing) return { effects: [], cooldown: 3 };

  const effects = [];
  const pCenterX = player.x + TILE_SIZE / 2;
  const pCenterY = player.y + TILE_SIZE / 2;

  // Dark green afterimage at start position
  effects.push({ type: 'afterimage', x: player.x, y: player.y, color: 0x44cc44, duration: 0.4 });

  // Find nearest enemy within 80px
  let nearestEnemy = null;
  let nearestDist = Infinity;
  for (const enemy of enemies) {
    if (!enemy.isAlive || enemy.state === 'dead') continue;
    const eCenterX = enemy.x + TILE_SIZE / 2;
    const eCenterY = enemy.y + TILE_SIZE / 2;
    const d = dist(pCenterX, pCenterY, eCenterX, eCenterY);
    if (d <= 80 && d < nearestDist) {
      nearestDist = d;
      nearestEnemy = enemy;
    }
  }

  if (nearestEnemy) {
    // Teleport behind the enemy (opposite side from player approach)
    const eCenterX = nearestEnemy.x + TILE_SIZE / 2;
    const eCenterY = nearestEnemy.y + TILE_SIZE / 2;
    // Direction from player to enemy
    const toEnemyX = eCenterX - pCenterX;
    const toEnemyY = eCenterY - pCenterY;
    const toEnemyLen = Math.sqrt(toEnemyX * toEnemyX + toEnemyY * toEnemyY);
    const toNx = toEnemyLen > 0 ? toEnemyX / toEnemyLen : facing.nx;
    const toNy = toEnemyLen > 0 ? toEnemyY / toEnemyLen : facing.ny;

    // Behind the enemy = enemy position + direction vector * TILE_SIZE
    let destX = nearestEnemy.x + toNx * TILE_SIZE;
    let destY = nearestEnemy.y + toNy * TILE_SIZE;

    if (!canOccupy(destX, destY, player.size, tiles)) {
      const fallback = findNearestWalkable(destX, destY, player.size, tiles, TILE_SIZE * 2);
      if (fallback) {
        destX = fallback.x;
        destY = fallback.y;
      } else {
        destX = player.x;
        destY = player.y;
      }
    }

    player.x = destX;
    player.y = destY;
    player._syncVisual();

    // Update player facing toward the enemy
    const newDx = eCenterX - (destX + TILE_SIZE / 2);
    const newDy = eCenterY - (destY + TILE_SIZE / 2);
    const newLen = Math.sqrt(newDx * newDx + newDy * newDy);
    if (newLen > 0) {
      player.facingX = newDx / newLen;
      player.facingY = newDy / newLen;
    }

    // Deal 30 backstab damage
    const kbNx = -toNx;
    const kbNy = -toNy;
    nearestEnemy.takeDamage(30, kbNx, kbNy);
    effects.push({ type: 'damage', target: nearestEnemy, amount: 30, x: eCenterX, y: eCenterY });

    // 1s invulnerability
    player.hitInvulnTimer = Math.max(player.hitInvulnTimer, 1.0);

    // Flash at arrival
    effects.push({ type: 'flash_circle', x: destX + TILE_SIZE / 2, y: destY + TILE_SIZE / 2, radius: 16, color: 0x44cc44, duration: 0.25 });
  } else {
    // No enemy nearby: dash forward 48px
    const dashDist = 48;
    let destX = player.x + facing.nx * dashDist;
    let destY = player.y + facing.ny * dashDist;

    if (!canOccupy(destX, destY, player.size, tiles)) {
      const fallback = findNearestWalkable(destX, destY, player.size, tiles, dashDist);
      if (fallback) {
        destX = fallback.x;
        destY = fallback.y;
      } else {
        destX = player.x;
        destY = player.y;
      }
    }

    player.x = destX;
    player.y = destY;
    player._syncVisual();

    // 1s invulnerability
    player.hitInvulnTimer = Math.max(player.hitInvulnTimer, 1.0);

    // Flash at arrival
    effects.push({ type: 'flash_circle', x: destX + TILE_SIZE / 2, y: destY + TILE_SIZE / 2, radius: 16, color: 0x44cc44, duration: 0.25 });
  }

  return { effects, cooldown: 3 };
}

// ============================================================
// Voidpriest -- Radiant Ward
// ============================================================

function executeRadiantWard(player, enemies, tiles) {
  const effects = [];
  const pCenterX = player.x + TILE_SIZE / 2;
  const pCenterY = player.y + TILE_SIZE / 2;

  // Create shield that absorbs 40 damage
  player.shield = 40;

  // Heal 15 HP
  const oldHp = player.hp;
  player.hp = Math.min(player.maxHp, player.hp + 15);
  const healed = player.hp - oldHp;
  if (healed > 0) {
    effects.push({ type: 'heal', amount: healed, x: pCenterX, y: pCenterY });
  }

  // All enemies within 48px radius take 10 damage and are pushed back 16px
  const wardRadius = 48;
  for (const enemy of enemies) {
    if (!enemy.isAlive || enemy.state === 'dead') continue;
    const eCenterX = enemy.x + TILE_SIZE / 2;
    const eCenterY = enemy.y + TILE_SIZE / 2;
    const d = dist(pCenterX, pCenterY, eCenterX, eCenterY);
    if (d <= wardRadius) {
      // Pushback direction: away from player
      const pushDx = eCenterX - pCenterX;
      const pushDy = eCenterY - pCenterY;
      const pushLen = Math.sqrt(pushDx * pushDx + pushDy * pushDy);
      const pushNx = pushLen > 0 ? pushDx / pushLen : 0;
      const pushNy = pushLen > 0 ? pushDy / pushLen : 1;

      // Deal 10 damage with strong knockback
      enemy.takeDamage(10, pushNx * 2, pushNy * 2);

      effects.push({ type: 'damage', target: enemy, amount: 10, x: eCenterX, y: eCenterY });
    }
  }

  // Visual: golden expanding circle
  effects.push({ type: 'flash_circle', x: pCenterX, y: pCenterY, radius: wardRadius, color: 0xffcc44, duration: 0.5 });

  // Visual: yellow glow on player
  effects.push({ type: 'player_glow', color: 0xffcc44, duration: 2.0 });

  return { effects, cooldown: 6 };
}
